import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import {
    PhysicalUnit, WeeklyPlan, ProtocolConfig, CompanyConfig, ProtocolState, Contract, PaymentStatement,
    ProjectInfo, Responsible, Profile, Activity, Task, ProjectProgressSnapshot, LookaheadPlan, GanttBaseline
} from '../src/types';
import {
    calculateSCurve, SCurvePoint, calculateWeightedProgress,
    calculateProjectProgress, truncateProgress, calculateDelayDays, calculateMultiSCurve, MultiSCurvePoint,
    calculateProgrammedProgressAtDate, calculateCriticalPathDelayAtDate
} from '../src/utils/planningUtils';

import { NotificationBell } from '../src/components/ui/NotificationBell';
import { MobileTitleBar } from '../src/components/mobile/MobileTitleBar';
import { PullToRefresh } from '../src/components/mobile/PullToRefresh';
import { KlDialog } from '../src/components/mobile/modal/KlDialog';
import { MobileSubcontractorPickerSheet } from '../src/components/mobile/payments/MobileSubcontractorPickerSheet';

interface DashboardScreenProps {
    projectInfo: ProjectInfo;
    tasks: Task[];
    responsibles: Responsible[];
    savedProtocols: Record<string, ProtocolState>;
    contracts: Contract[];
    paymentStatements: PaymentStatement[];
    activities: Activity[];
    progressHistory: ProjectProgressSnapshot[];
    units: PhysicalUnit[];
    matrixData: Record<string, any>;
    weeklyPlans: WeeklyPlan[];
    protocolConfigs: Record<string, ProtocolConfig>;
    companyConfig: CompanyConfig;
    lookaheadPlans: LookaheadPlan[];
    notifications?: any;
    setNotifications?: any;
    currentUser?: any;
    hasPermission?: (module: string, action: 'view'|'edit') => boolean;
    subcontractors?: any[];
    isNative?: boolean;
    baselines?: GanttBaseline[];
}

const DashboardScreen: React.FC<DashboardScreenProps> = ({
    projectInfo, tasks, responsibles, savedProtocols, contracts, paymentStatements,
    activities, progressHistory, units, matrixData, weeklyPlans, protocolConfigs, companyConfig, lookaheadPlans,
    notifications, setNotifications, currentUser, hasPermission = () => true, subcontractors = [], isNative = false,
    baselines = []
}) => {
    const navigate = useNavigate();
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [selectedMobileSubcontractorId, setSelectedMobileSubcontractorId] = useState<string>('');
    const [isSubcontractorPickerOpen, setIsSubcontractorPickerOpen] = useState(false);
    const [pdfErrorDialog, setPdfErrorDialog] = useState<string | null>(null);

    React.useEffect(() => {
        if (!selectedMobileSubcontractorId && contracts.length > 0) {
            const availableSubIds = Array.from(new Set(contracts.filter(c => c.status !== 'Anulado').map(c => c.subcontractorId)))
                .filter(subId => {
                    if (currentUser?.globalRole === 'Administrador') return true;
                    const sub = subcontractors.find(s => s.id === subId);
                    return sub?.linkedUserIds?.includes(currentUser?.id || '');
                })
                .sort((a, b) => {
                    const subA = subcontractors.find(s => s.id === a);
                    const subB = subcontractors.find(s => s.id === b);
                    return (subA?.razonSocial || '').localeCompare(subB?.razonSocial || '');
                });

            if (availableSubIds.length > 0) {
                setSelectedMobileSubcontractorId(availableSubIds[0]);
            }
        }
    }, [contracts, subcontractors, currentUser, selectedMobileSubcontractorId]);

    const downloadPDF = async () => {
        const input = document.getElementById('dashboard-content');
        if (!input) return;
        setIsGeneratingPdf(true);
        try {
            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const margin = 25.4; // 25.4 mm (1 pulgada, estándar de impresión)
            const pdfWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            
            pdf.addImage(imgData, 'PNG', margin, margin, pdfWidth, pdfHeight);
            pdf.save(`Dashboard_${projectInfo?.name?.replace(/\s+/g, '_') || 'K-LyDapp'}.pdf`);
        } catch (error) {
            console.error('Error generando PDF', error);
            setPdfErrorDialog('Hubo un error al generar el PDF. Asegúrate de intentar nuevamente.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };
    // --- Calculations ---

    // 1. Project Progress (Weighted Average)
    // 1. Project Progress (Use last synchronized history)
    const projectProgress = useMemo(() => {
        if (progressHistory && progressHistory.length > 0) {
            let validHistory = progressHistory.filter(h => h && h.date && h.progress !== undefined && h.progress > 0);
            
            validHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));

            if (validHistory.length > 0) {
                return validHistory[validHistory.length - 1].progress || 0;
            }
        }
        return 0; // Fallback to 0 if no history
    }, [progressHistory]);

    // 2. Subcontracts Health
    const activeSubcontracts = contracts.filter(c => c.status === 'Activo').length;
    const totalContractedAmount = contracts.filter(c => c.status !== 'Anulado').reduce((sum, c) => sum + (c.totalAmount || 0), 0);
    const totalPaidAmount = paymentStatements
        .filter(ps => ps.status === 'Aprobado' || ps.status === 'Autorizado')
        .reduce((sum, ps) => {
            const amtStr = ps.amount || '0';
            return sum + (parseFloat(amtStr.replace(/\./g, "").replace(/[^0-9-]/g, "")) || 0);
        }, 0);

    // 3. Quality Metrics
    const qualityMetrics = useMemo(() => {
        const stats = { open: 0, reviewing: 0, observations: 0, closed: 0 };
        (Object.values(savedProtocols) as ProtocolState[]).forEach(p => {
            if (p.status === 'Abierto' || p.status === 'Resuelto') stats.open++;
            else if (p.status === 'Rechazado') stats.observations++;
            else if (p.status === 'Cerrado') stats.closed++;

            // "In Review" logic: if it's open or resolved but has at least one signature
            if ((p.status === 'Abierto' || p.status === 'Resuelto') && Object.keys(p.signatures || {}).length > 0) {
                stats.reviewing++;
                stats.open--;
            }
        });
        return stats;
    }, [savedProtocols]);

    const workingDays = useMemo<boolean[]>(() => {
        try {
            const saved = localStorage.getItem(`gantt_working_days_${projectInfo?.id || 'default'}`);
            return saved ? JSON.parse(saved) : [false, true, true, true, true, true, false];
        } catch {
            return [false, true, true, true, true, true, false];
        }
    }, [projectInfo?.id]);

    const holidays = useMemo<{ date: string; name: string }[]>(() => {
        try {
            const saved = localStorage.getItem(`gantt_holidays_${projectInfo?.id || 'default'}`);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    }, [projectInfo?.id]);

    const getCriticalPathDelayAtDate = useCallback((dateStr: string): number => {
        return calculateCriticalPathDelayAtDate(
            tasks,
            dateStr,
            units,
            matrixData,
            protocolConfigs,
            projectInfo,
            savedProtocols,
            weeklyPlans,
            workingDays,
            holidays
        );
    }, [tasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, workingDays, holidays]);

    // 4. S-Curve Data (Real calculation based on tasks)
    const curveData = useMemo(() => {
        const virtualHistory = [...(progressHistory || [])];
        return calculateMultiSCurve(tasks, baselines, virtualHistory, 5);
    }, [tasks, baselines, progressHistory]);

    // Custom Tooltip for S-Curve
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const syncRecord = progressHistory.find(h => {
                const hTime = new Date(h.date + "T00:00:00").getTime();
                const ptTime = new Date(data.date + "T00:00:00").getTime();
                return hTime >= ptTime && hTime < ptTime + 7 * 86400000;
            });

            return (
                <div className="bg-[#1e293b] p-3 rounded-lg border border-slate-700 shadow-xl text-white text-[11px] flex flex-col gap-1.5 min-w-[200px]">
                    <div className="flex justify-between items-center border-b border-slate-700 pb-1.5 mb-0.5">
                        <span className="font-bold text-slate-300">Semana {data.weekIndex}</span>
                        <span className="text-[10px] text-slate-400">({new Date(data.date + "T12:00:00").toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })})</span>
                    </div>
                    {payload.map((pld: any) => {
                        let valColor = pld.color || '#cbd5e1';
                        let label = pld.name;
                        // Map standard keys to friendly labels if not set
                        if (pld.dataKey === 'planActive') {
                            const activeB = baselines.find(b => b.vigente);
                            label = activeB ? `Progr. Rev ${activeB.versionNumber} (Vigente)` : 'Progr. Vigente';
                            valColor = '#1a73e8'; // Blue
                        } else if (pld.dataKey === 'real') {
                            label = 'Avance Real';
                            valColor = '#10b981'; // Green
                        } else if (typeof pld.dataKey === 'string' && pld.dataKey.startsWith('plan_rev')) {
                            const revNum = pld.dataKey.replace('plan_rev', '');
                            label = `Progr. Rev ${revNum}`;
                        }
                        
                        return (
                            <div key={pld.dataKey} className="flex justify-between items-center gap-4">
                                <span className="text-slate-400" style={{ color: pld.color }}>{label}:</span>
                                <span className="font-bold" style={{ color: valColor }}>
                                    {pld.value !== null && pld.value !== undefined ? `${truncateProgress(pld.value)}%` : '--'}
                                </span>
                            </div>
                        );
                    })}
                    {syncRecord && (
                        <>
                            <div className="h-[1px] bg-slate-700 my-0.5" />
                            <div className="flex justify-between items-center">
                                <span className="text-slate-400">Atraso Ruta Crítica:</span>
                                <span className={`font-bold ${getCriticalPathDelayAtDate(syncRecord.date) > 0 ? 'text-orange-400' : 'text-slate-300'}`}>
                                    {getCriticalPathDelayAtDate(syncRecord.date)} días
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-slate-400">Atraso Curva S:</span>
                                <span className={`font-bold ${syncRecord.delayCurvaS && syncRecord.delayCurvaS > 0 ? 'text-red-400' : 'text-slate-300'}`}>
                                    {syncRecord.delayCurvaS || 0} días
                                </span>
                            </div>
                        </>
                    )}
                </div>
            );
        }
        return null;
    };

    // 5. Project Delay Status (Calculado Dinámicamente "En Vivo" al día de hoy)
    const projectStatus = useMemo(() => {
        if (!tasks || tasks.length === 0) {
            return { label: 'Al día', color: 'bg-[#52be80] border-[#229954] text-white', icon: 'check_circle', delay: 0 };
        }
        
        // 1. Calcular el progreso acumulado real actual en vivo
        const liveProgress = calculateProjectProgress(
            tasks,
            units,
            matrixData,
            protocolConfigs,
            projectInfo,
            savedProtocols,
            weeklyPlans
        );

        // 2. Obtener la fecha de hoy en huso horario local
        const todayStr = new Date().toISOString().split('T')[0];

        // 3. Calcular la desviación dinámica en base al progreso en vivo
        const calculatedDelay = calculateDelayDays(curveData, liveProgress, todayStr);

        // 4. Considerar también la ruta crítica si existen snapshots previos
        let delayRutaCritica = 0;
        if (progressHistory && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date);
            if (validHistory.length > 0) {
                const lastRecord = [...validHistory].sort((a, b) => b.date.localeCompare(a.date))[0];
                delayRutaCritica = getCriticalPathDelayAtDate(lastRecord.date);
            }
        }

        const delay = Math.max(calculatedDelay, delayRutaCritica);

        if (delay <= 0) {
            return { label: 'Al día', color: 'bg-[#52be80] border-[#229954] text-white', icon: 'check_circle', delay: 0 };
        } else if (delay <= 9) {
            return { label: 'En Riesgo', color: 'bg-[#f4d03f] border-[#d4ac0d] text-slate-900', icon: 'warning', delay };
        } else {
            return { label: 'Atrasado', color: 'animate-pulse bg-[#ff0000] border-[#cc0000] text-yellow-300', icon: 'error', delay };
        }
    }, [tasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, curveData, progressHistory, getCriticalPathDelayAtDate]);

    // Helper to filter 'En Planificación' when user lacks review permission
    const isPlanVisible = useCallback((status?: string) => {
        if (!status) return true;
        if (status === 'En Planificación') {
            const canReview = hasPermission('movil_ps_revisar', 'view') || hasPermission('ps_revisar', 'view') || hasPermission('movil_ps_revisar', 'edit') || hasPermission('ps_revisar', 'edit');
            return canReview;
        }
        return true;
    }, [hasPermission]);

    // --- MOBILE METRICS ---
    const lookaheadMetrics = useMemo(() => {
        let open = 0;
        let closed = 0;
        let pcr = 0;
        let planNumber: string | number = 0;
        let hasPlan = false;
        const visibleLookahead = (lookaheadPlans || []).filter(p => p && isPlanVisible(p.status));
        if (visibleLookahead.length > 0) {
            const sortedPlans = [...visibleLookahead].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
            const activePlan = sortedPlans.find(lp => lp && lp.status === 'En Ejecución') || sortedPlans[0];
            if (activePlan) {
                hasPlan = true;
                const chronologicalPlans = [...visibleLookahead].sort((a,b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
                const index = chronologicalPlans.findIndex(p => p.id === activePlan.id);
                planNumber = index !== -1 ? index + 1 : (Number(activePlan.id) || 1);
                
                if (Array.isArray(activePlan.constraints)) {
                    activePlan.constraints.forEach(c => {
                        if (c.status === 'Liberado') closed++;
                        else open++;
                    });
                }
                const total = open + closed;
                pcr = total > 0 ? Math.round((closed / total) * 100) : 100;
            }
        }
        return { open, closed, pcr, planNumber, hasPlan };
    }, [lookaheadPlans, isPlanVisible]);

    const weeklyMetrics = useMemo(() => {
        let executed = 0;
        let pending = 0;
        let ppc = 0;
        let planNumber: string | number = 0;
        let hasPlan = false;
        const visibleWeekly = (weeklyPlans || []).filter(p => p && isPlanVisible(p.status));
        if (visibleWeekly.length > 0) {
            const sortedPlans = [...visibleWeekly].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
            const activeWeekly = sortedPlans.find(wp => wp && wp.status === 'En Ejecución') || sortedPlans.find(wp => wp && wp.status === 'Cerrada') || sortedPlans[0];
            if (activeWeekly) {
                hasPlan = true;
                planNumber = activeWeekly.id;
                if (Array.isArray(activeWeekly.allocations)) {
                    activeWeekly.allocations.forEach(alloc => {
                        if (alloc.executed) executed++;
                        else pending++;
                    });
                }
                const total = executed + pending;
                ppc = total > 0 ? Math.round((executed / total) * 100) : 0;
            }
        }
        return { executed, pending, ppc, planNumber, hasPlan };
    }, [weeklyPlans, isPlanVisible]);

    const currentPayment = useMemo(() => {
        if (!paymentStatements || !Array.isArray(paymentStatements)) return '$0';
        const sorted = [...paymentStatements].filter(ps => ps && ps.status !== 'Anulado' && ps.startDate).sort((a,b) => new Date(b.startDate || '').getTime() - new Date(a.startDate || '').getTime());
        if (sorted.length > 0) {
             const amt = sorted[0].amount;
             const parsed = parseFloat(typeof amt === 'string' ? amt.replace(/\./g, "").replace(/[^0-9-]/g, "") : String(amt || 0));
             return formatCurrency(parsed || 0);
        }
        return '$0';
    }, [paymentStatements]);

    const latestProgressRecord = useMemo(() => {
        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date && h.progress !== undefined && h.progress > 0);
            if (validHistory.length > 0) {
                return [...validHistory].sort((a, b) => b.date.localeCompare(a.date))[0];
            }
        }
        return null;
    }, [progressHistory]);
    const delayCS = useMemo(() => {
        if (!latestProgressRecord) return 0;
        if (latestProgressRecord.delayCurvaS && latestProgressRecord.delayCurvaS > 0) return latestProgressRecord.delayCurvaS;
        return calculateDelayDays(curveData, latestProgressRecord.progress, latestProgressRecord.date);
    }, [latestProgressRecord, curveData]);

    const availableSubcontractors = useMemo(() => {
        const subIds = Array.from(new Set(contracts.filter(c => c && c.status !== 'Anulado').map(c => c.subcontractorId)))
            .filter(subId => {
                if (!subId) return false;
                if (currentUser?.globalRole === 'Administrador') return true;
                const sub = subcontractors.find(s => s.id === subId);
                return sub ? (sub.linkedUserIds?.includes(currentUser?.id || '') ?? true) : true;
            });

        return subIds.map(subId => {
            const found = subcontractors.find(s => s.id === subId);
            return {
                id: subId,
                razonSocial: found?.razonSocial || found?.name || `Subcontratista (${subId.substring(0, 8)})`
            };
        }).sort((a, b) => (a.razonSocial || '').localeCompare(b.razonSocial || ''));
    }, [contracts, subcontractors, currentUser]);

    const selectedSubcontractorName = useMemo(() => {
        const targetId = selectedMobileSubcontractorId || availableSubcontractors[0]?.id;
        if (!targetId) return '';
        const found = availableSubcontractors.find(s => s.id === targetId);
        return found?.razonSocial || '';
    }, [availableSubcontractors, selectedMobileSubcontractorId]);

    const mobileSubcontractorMetrics = useMemo(() => {
        let totalContracted = 0;
        let totalPaid = 0;
        let currentEP = '$0';
        
        const targetSubId = selectedMobileSubcontractorId || availableSubcontractors[0]?.id;

        if (targetSubId) {
            const subContracts = contracts.filter(c => c.subcontractorId === targetSubId && c.status !== 'Anulado');
            totalContracted = subContracts.reduce((sum, c) => sum + (c.totalAmount || 0), 0);
            
            const subEPs = paymentStatements.filter(ps => ps.subcontractorId === targetSubId && (ps.status === 'Aprobado' || ps.status === 'Autorizado'));
            totalPaid = subEPs.reduce((sum, ps) => {
                const amtStr = ps.amount || '0';
                return sum + (parseFloat(amtStr.replace(/\./g, "").replace(/[^0-9-]/g, "")) || 0);
            }, 0);
            
            let totalPayable = 0;
            subContracts.forEach(contract => {
                const validEPs = paymentStatements.filter(ep => ep.contractId === contract.id && ep.status !== 'Anulado' && ep.status !== 'Borrador');
                const consolidatedItemsMap = new Map();
                (contract.items || []).forEach(item => consolidatedItemsMap.set(item.id, JSON.parse(JSON.stringify(item))));
                (contract.anexos || []).filter(a => a.status === 'Activo').forEach(anexo => {
                    (anexo.items || []).forEach(aItem => {
                        if (consolidatedItemsMap.has(aItem.id)) {
                            const existing = consolidatedItemsMap.get(aItem.id);
                            aItem.details.forEach(d => {
                                const eIdx = existing.details.findIndex(ed => ed.unitId === d.unitId);
                                if (eIdx >= 0) existing.details[eIdx] = d; else existing.details.push(d);
                            });
                            existing.totalValue = existing.details.reduce((sum, d) => sum + d.value, 0);
                        } else {
                            consolidatedItemsMap.set(aItem.id, JSON.parse(JSON.stringify(aItem)));
                        }
                    });
                });

                consolidatedItemsMap.forEach(item => {
                    (item.details || []).forEach(detail => {
                        const isAlreadyPaid = validEPs.some(ep => 
                            ep.items?.some(i => i.id === item.id && i.selectedUnitIds?.includes(detail.unitId))
                        );
                        
                        if (!isAlreadyPaid) {
                            const isPayable = Object.values(savedProtocols).some((p: ProtocolState) => {
                                if (p.taskId === item.id && p.unitId === detail.unitId) {
                                    const config = protocolConfigs[item.id];
                                    const group = p.groupId
                                        ? config?.reviewerGroups?.find(g => g.id === p.groupId)
                                        : config?.reviewerGroups?.find(g => g.unitIds.includes(p.unitId));

                                    if (group) {
                                        return group.reviewers.some((rev, idx) =>
                                            rev.generatesPayment && p.signatures[idx]
                                        );
                                    }
                                }
                                return false;
                            });
                            if (isPayable) {
                                totalPayable += detail.value;
                            }
                        }
                    });
                });
            });
            currentEP = formatCurrency(totalPayable);
        }
        
        const paidPercent = totalContracted > 0 ? Math.round((totalPaid / totalContracted) * 100) : 0;
        return {
            totalContracted: formatCurrency(totalContracted),
            totalPaid: formatCurrency(totalPaid),
            currentEP,
            paidPercent
        };
    }, [contracts, paymentStatements, selectedMobileSubcontractorId, availableSubcontractors, savedProtocols, protocolConfigs]);

    function formatCurrency(val: number) {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
    }

    return (
        <div className="flex flex-1 flex-col h-full overflow-hidden relative">
            {/* ======================================================== */}
            {/* VISTA MÓVIL (01-panel.html)                              */}
            {/* ======================================================== */}
            <PullToRefresh className="block md:hidden flex-1" style={{ background: 'var(--kl-bg, #F1F5F9)' }}>
                {/* 1. MobileTitleBar */}
                <MobileTitleBar
                    title="Panel de Control"
                    projectName={projectInfo.name}
                    projectCode={projectInfo.code}
                    delayDays={projectStatus.delay}
                    delayStatusText={projectStatus.delay > 0 ? `Atrasado · ${projectStatus.delay}d` : (projectStatus.label || 'Al día')}
                />

                {/* 2. Contenedor de Tarjetas */}
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Fila 1: Intermedia y Semanal en 2 columnas */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {/* Card Intermedia */}
                        <div
                            className="c"
                            onClick={() => {
                                let activePlanId = null;
                                const visibleLookahead = (lookaheadPlans || []).filter(p => p && isPlanVisible(p.status));
                                if (visibleLookahead.length > 0) {
                                    const sortedPlans = [...visibleLookahead].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
                                    const activeLP = sortedPlans.find(lp => lp && lp.status === 'En Ejecución') || sortedPlans[0];
                                    if (activeLP) activePlanId = activeLP.id;
                                }
                                navigate('/planning', { state: { activeTab: 'intermedia', activeLookaheadId: activePlanId } });
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="th">
                                <span className="ic" style={{ background: '#EFF6FF', color: '#2563EB' }}>
                                    <svg viewBox="0 0 24 24"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                                </span>
                                <b style={{ fontSize: lookaheadMetrics.hasPlan ? '13px' : '11px', lineHeight: 1.2 }}>
                                    {lookaheadMetrics.hasPlan ? `Intermedia #${lookaheadMetrics.planNumber}` : 'Sin planificación intermedia'}
                                </b>
                            </div>
                            {lookaheadMetrics.hasPlan ? (
                                <>
                                    <div className="bg">
                                        <b>{lookaheadMetrics.pcr}%</b>
                                        <span>PCR</span>
                                    </div>
                                    <div className="pr">
                                        <i style={{ width: `${lookaheadMetrics.pcr}%`, background: '#16A34A' }}></i>
                                    </div>
                                    <div className="ch">
                                        <i style={{ background: '#D97706' }}></i>Abiertas<b>{lookaheadMetrics.open}</b>
                                    </div>
                                    <div className="ch">
                                        <i style={{ background: '#16A34A' }}></i>Cerradas<b>{lookaheadMetrics.closed}</b>
                                    </div>
                                </>
                            ) : (
                                <div style={{ marginTop: '14px', fontSize: '11px', color: '#64748B', lineHeight: '1.4' }}>
                                    Sin restricciones programadas
                                </div>
                            )}
                        </div>

                        {/* Card Semanal */}
                        <div
                            className="c"
                            onClick={() => {
                                let activePlanId = null;
                                const visibleWeekly = (weeklyPlans || []).filter(p => p && isPlanVisible(p.status));
                                if (visibleWeekly.length > 0) {
                                    const sortedPlans = [...visibleWeekly].sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
                                    const activeWeekly = sortedPlans.find(wp => wp && wp.status === 'En Ejecución') || sortedPlans.find(wp => wp && wp.status === 'Cerrada') || sortedPlans[0];
                                    if (activeWeekly) activePlanId = activeWeekly.id;
                                }
                                navigate('/planning', { state: { activeTab: 'semanal', activePlanId } });
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="th">
                                <span className="ic" style={{ background: '#F5F3FF', color: '#6D28D9' }}>
                                    <svg viewBox="0 0 24 24"><path d="M3 6l2 2 3-3M3 14l2 2 3-3M12 7h9M12 15h9"/></svg>
                                </span>
                                <b style={{ fontSize: weeklyMetrics.hasPlan ? '13px' : '11px', lineHeight: 1.2 }}>
                                    {weeklyMetrics.hasPlan ? `Semanal #${weeklyMetrics.planNumber}` : 'Sin planificación semanal'}
                                </b>
                            </div>
                            {weeklyMetrics.hasPlan ? (
                                <>
                                    <div className="bg">
                                        <b>{weeklyMetrics.ppc}%</b>
                                        <span>PPC</span>
                                    </div>
                                    <div className="pr">
                                        <i style={{ width: `${weeklyMetrics.ppc}%`, background: '#6D28D9' }}></i>
                                    </div>
                                    <div className="ch">
                                        <i style={{ background: '#16A34A' }}></i>Finalizadas<b>{weeklyMetrics.executed}</b>
                                    </div>
                                    <div className="ch">
                                        <i style={{ background: '#D97706' }}></i>Pendientes<b>{weeklyMetrics.pending}</b>
                                    </div>
                                </>
                            ) : (
                                <div style={{ marginTop: '14px', fontSize: '11px', color: '#64748B', lineHeight: '1.4' }}>
                                    Sin tareas programadas
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Card Calidad (Punto 1) */}
                    {(hasPermission("cc_config", "view") || hasPermission("mod_movil_calidad", "view")) && (
                        <div
                            className="kl-card"
                            onClick={() => navigate('/quality')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="th">
                                <span className="ic" style={{ background: '#FFF7ED', color: '#EA580C' }}>
                                    <svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
                                </span>
                                <b style={{ fontSize: '13px' }}>Control de Calidad</b>
                            </div>
                            <div className="bg">
                                <b>{(Object.values(qualityMetrics) as number[]).reduce((a, b) => a + b, 0)}</b>
                                <span>Protocolos</span>
                            </div>
                            <div className="ch">
                                <i style={{ background: '#2563EB' }}></i>Abiertos<b>{qualityMetrics.open}</b>
                            </div>
                            <div className="ch">
                                <i style={{ background: '#D97706' }}></i>En Revisión<b>{qualityMetrics.reviewing}</b>
                            </div>
                            <div className="ch">
                                <i style={{ background: '#DC2626' }}></i>Con Observaciones<b>{qualityMetrics.observations}</b>
                            </div>
                            <div className="ch">
                                <i style={{ background: '#16A34A' }}></i>Cerrados<b>{qualityMetrics.closed}</b>
                            </div>
                        </div>
                    )}

                    {/* Card Subcontratos */}
                    <div
                        className="c"
                        onClick={() => navigate('/payments')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="th" style={{ marginBottom: '10px' }}>
                            <span className="ic" style={{ background: '#ECFDF5', color: '#047857' }}>
                                <svg viewBox="0 0 24 24"><path d="M3 7h18v12H3zM3 7l3-3h12v3M16 13h2"/></svg>
                            </span>
                            <b style={{ fontSize: '14px' }}>Subcontratos</b>
                        </div>

                        {/* Selector de subcontrato */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsSubcontractorPickerOpen(true);
                            }}
                            style={{
                                position: 'relative',
                                height: '40px',
                                border: '1px solid #E2E8F0',
                                borderRadius: '10px',
                                background: '#F8FAFC',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0 10px',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#334155',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '20px' }}>
                                {selectedSubcontractorName || 'Seleccionar Subcontrato...'}
                            </span>
                            <svg viewBox="0 0 24 24" className="i" style={{ width: '18px', height: '18px', flexShrink: 0, position: 'absolute', right: '10px', pointerEvents: 'none' }}>
                                <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>

                        {/* Montos en 3 columnas */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr', gap: '8px', marginTop: '12px' }}>
                            <div>
                                <div className="lb">Contratado</div>
                                <div className="v">{mobileSubcontractorMetrics.totalContracted}</div>
                            </div>
                            <div>
                                <div className="lb" style={{ color: '#1D4ED8' }}>Pagado</div>
                                <div className="v" style={{ color: '#1D4ED8' }}>{mobileSubcontractorMetrics.totalPaid}</div>
                            </div>
                            <div>
                                <div className="lb" style={{ color: '#047857' }}>Pago actual</div>
                                <div className="v" style={{ color: '#047857' }}>{mobileSubcontractorMetrics.currentEP}</div>
                            </div>
                        </div>

                        {/* Barra de progreso y texto % pagado */}
                        <div className="pr" style={{ marginTop: '10px' }}>
                            <i style={{ width: `${mobileSubcontractorMetrics.paidPercent}%`, background: '#2563EB' }}></i>
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px' }}>
                            Pagado {mobileSubcontractorMetrics.paidPercent}% del contrato
                        </div>
                    </div>

                    {/* Espaciador inferior para scroll holgado */}
                    <div className="kl-list-end" style={{ height: '140px', flexShrink: 0 }} />
                </div>

                <MobileSubcontractorPickerSheet
                    open={isSubcontractorPickerOpen}
                    onClose={() => setIsSubcontractorPickerOpen(false)}
                    subcontractors={availableSubcontractors as any}
                    onPickSubcontractor={(sub) => {
                        setSelectedMobileSubcontractorId(sub.id);
                        setIsSubcontractorPickerOpen(false);
                    }}
                />

                {pdfErrorDialog && (
                    <KlDialog
                        open={!!pdfErrorDialog}
                        onClose={() => setPdfErrorDialog(null)}
                        title="Error al generar PDF"
                        message={pdfErrorDialog}
                        confirmText="Entendido"
                        cancelText={null}
                        onConfirm={() => setPdfErrorDialog(null)}
                    />
                )}
            </PullToRefresh>

            {/* ======================================================== */}
            {/* VISTA ESCRITORIO (100% INTACTA)                          */}
            {/* ======================================================== */}
            <div className="hidden md:flex flex-col flex-1 h-full overflow-hidden relative">
                <header className="flex h-16 w-full items-center justify-between border-b border-[#e7ecf3] bg-white px-6 dark:bg-[#1a2634] dark:border-[#2a3644] flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="flex w-96 max-w-lg items-center rounded-lg bg-[#f3f4f6] dark:bg-[#2a3644] px-3 py-2">
                            <span className="material-symbols-outlined text-slate-400 text-[20px]">search</span>
                            <input className="ml-2 w-full bg-transparent text-sm text-slate-900 placeholder-slate-400 focus:outline-none dark:text-white border-none focus:ring-0" placeholder="Buscar proyectos, tareas..." type="text" />
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3 border-r border-slate-200 dark:border-slate-700 pr-6 mr-2">
                            <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt={companyConfig?.name || 'Constructora LD'} className="hidden md:block h-8 w-auto object-contain" />
                            <h2 className="text-lg font-black text-slate-800 dark:text-white tracking-tight uppercase leading-none">{companyConfig?.name}</h2>
                        </div>

                        <div className="flex items-center gap-4">
                            {notifications && setNotifications && (
                                <NotificationBell notifications={notifications} setNotifications={setNotifications} currentUser={currentUser} />
                            )}
                            <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-700 mx-1"></div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto bg-background-light dark:bg-background-dark p-4 md:p-6 lg:p-10">
                    <div id="dashboard-content" className="mx-auto max-w-7xl flex flex-col gap-6 md:gap-8 pb-10 bg-background-light dark:bg-background-dark">
                        
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                                <span className="hover:text-primary transition-colors cursor-pointer">Proyectos</span>
                                <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                <span className="font-medium text-slate-500 hover:text-primary transition-colors cursor-pointer">{projectInfo.name}</span>
                                <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                <span className="font-medium text-slate-900 dark:text-white">Dashboard</span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-lg md:text-3xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                                        <span>Resumen de la Obra</span>
                                        <div className={`flex items-center gap-1 px-2.5 py-0.5 md:px-3 md:py-1.5 rounded-full border text-[11px] md:text-sm font-semibold shadow-sm whitespace-nowrap shrink-0 ${projectStatus.color}`}>
                                            <span className="material-symbols-outlined text-[14px] md:text-[18px]">{projectStatus.icon}</span>
                                            <span>{projectStatus.label} {projectStatus.delay > 0 && `(${projectStatus.delay}d desvío)`}</span>
                                        </div>
                                    </h1>
                                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                                        Avance consolidado de obra
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={downloadPDF} disabled={isGeneratingPdf} className={`inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${isGeneratingPdf ? 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700' : 'bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#1a2634] dark:text-slate-200 dark:hover:bg-[#253242]'}`}>
                                        <span className={`material-symbols-outlined text-[20px] ${isGeneratingPdf ? 'animate-spin' : ''}`}>
                                            {isGeneratingPdf ? 'sync' : 'cloud_download'}
                                        </span>
                                        {isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
                            {/* 1. Project Progress */}
                            <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Avance de la Obra</p>
                                        <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{truncateProgress(projectProgress)}%</h3>
                                    </div>
                                    <div className="rounded-full bg-blue-50 p-2 dark:bg-blue-900/20">
                                        <span className="material-symbols-outlined text-primary text-[24px]">timelapse</span>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-col gap-2">
                                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                        <div className="absolute h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${projectProgress}%` }}></div>
                                    </div>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Basado en Planificación Maestra</p>
                                </div>
                            </div>

                            {/* 2. Subcontracts */}
                            <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Subcontratos Activos</p>
                                        <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{activeSubcontracts}</h3>
                                    </div>
                                    <div className="rounded-full bg-emerald-50 p-2 dark:bg-emerald-900/20">
                                        <span className="material-symbols-outlined text-emerald-600 text-[24px]">handshake</span>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-col gap-1 text-[11px]">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Contratado:</span>
                                        <span className="font-bold text-slate-700 dark:text-slate-300">{formatCurrency(totalContractedAmount)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Pagado:</span>
                                        <span className="font-bold text-emerald-600">{formatCurrency(totalPaidAmount)}</span>
                                    </div>
                                    <div className="flex justify-between border-t border-slate-100 dark:border-slate-700 pt-1 mt-1">
                                        <span className="text-slate-500">% Pagado:</span>
                                        <span className="font-bold text-blue-600">
                                            {totalContractedAmount > 0 ? Math.round((totalPaidAmount / totalContractedAmount) * 100) : 0}%
                                        </span>
                                    </div>
                                </div>
                            </div>

                        {/* 3. Quality Control (DESKTOP) */}
                        {(hasPermission("cc_config", "view") || hasPermission("mod_movil_calidad", "view")) && (
                        <div onClick={() => navigate('/quality')} className="hidden md:block cursor-pointer group rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634] hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Control de Calidad</p>
                                    <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{(Object.values(qualityMetrics) as number[]).reduce((a, b) => a + b, 0)} Protocolos</h3>
                                </div>
                                <div className="rounded-full bg-orange-50 p-2 dark:bg-orange-900/20">
                                    <span className="material-symbols-outlined text-orange-600 text-[24px]">fact_check</span>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Abiertos</span>
                                    <span className="text-sm font-bold text-blue-600">{qualityMetrics.open}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">En Revisión</span>
                                    <span className="text-sm font-bold text-orange-500">{qualityMetrics.reviewing}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Obs.</span>
                                    <span className="text-sm font-bold text-red-500">{qualityMetrics.observations}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Cerrados</span>
                                    <span className="text-sm font-bold text-green-600">{qualityMetrics.closed}</span>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* 4. Resumen Planificación Semanal */}
                        <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Planificación Semanal {weeklyMetrics.planNumber ? `#${weeklyMetrics.planNumber}` : ''}</p>
                                    <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                                        {weeklyMetrics.ppc}% PPC
                                    </h3>
                                </div>
                                <div className="rounded-full bg-indigo-50 p-2 dark:bg-indigo-900/20">
                                    <span className="material-symbols-outlined text-indigo-600 text-[24px]">checklist</span>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Finalizadas</span>
                                    <span className="text-sm font-bold text-emerald-600">{weeklyMetrics.executed}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Pendientes</span>
                                    <span className="text-sm font-bold text-orange-500">{weeklyMetrics.pending}</span>
                                </div>
                            </div>
                        </div>

                        {/* 5. Resumen Planificación Intermedia */}
                        <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Planificación Intermedia {lookaheadMetrics.planNumber ? `#${lookaheadMetrics.planNumber}` : ''}</p>
                                    <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{lookaheadMetrics.pcr}% PCR</h3>
                                </div>
                                <div className="rounded-full bg-blue-50 p-2 dark:bg-blue-900/20">
                                    <span className="material-symbols-outlined text-blue-600 text-[24px]">visibility</span>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Liberadas</span>
                                    <span className="text-sm font-bold text-emerald-500">{lookaheadMetrics.closed}</span>
                                </div>
                                <div className="flex flex-col p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded">
                                    <span className="text-[10px] text-slate-500 uppercase">Pendientes</span>
                                    <span className="text-sm font-bold text-orange-500">{lookaheadMetrics.open}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        {/* Curva S - HIDDEN ON MOBILE */}
                        <div className="hidden md:flex xl:col-span-2 flex-col gap-6">
                            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a2634]">
                                <div className="mb-6 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Curva S de Avance</h3>
                                        <p className="text-xs text-slate-500 italic">Programado vs Real Cumulativo</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-end max-w-md">
                                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                                            <span className="h-2.5 w-2.5 rounded-full bg-[#10b981]"></span> Avance Real
                                        </span>
                                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                                            <span className="h-2.5 w-2.5 rounded-full bg-[#1a73e8]"></span> {baselines.find(b => b.vigente) ? `Progr. Rev ${baselines.find(b => b.vigente)!.versionNumber} (Vigente)` : 'Progr. Vigente'}
                                        </span>
                                        {baselines.filter(b => b.selectedForChart && !b.vigente).map((b, idx) => {
                                            const colors = ['#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#ef4444'];
                                            const color = colors[idx % colors.length];
                                            return (
                                                <span key={b.id} className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500" style={{ opacity: 0.7 }}>
                                                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }}></span> Progr. Rev {b.versionNumber}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="h-72 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={curveData}>
                                            <defs>
                                                <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis
                                                dataKey="name"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 9, fill: '#94a3b8' }}
                                                minTickGap={30}
                                            />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} unit="%" />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area type="monotone" dataKey="planActive" stroke="#1a73e8" strokeWidth={3} fill="transparent" />
                                            {baselines.filter(b => b.selectedForChart && !b.vigente).map((b, idx) => {
                                                const colors = ['#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#ef4444'];
                                                const color = colors[idx % colors.length];
                                                return (
                                                    <Area
                                                        key={b.id}
                                                        type="monotone"
                                                        dataKey={`plan_rev${b.versionNumber}`}
                                                        stroke={color}
                                                        strokeWidth={2}
                                                        strokeDasharray="5 5"
                                                        fill="transparent"
                                                        strokeOpacity={0.3}
                                                    />
                                                );
                                            })}
                                            <Area type="monotone" dataKey="real" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorReal)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Tarjeta de Resumen Ejecutivo de la Semana Actual */}
                                {(() => {
                                    if (!progressHistory || progressHistory.length === 0) return null;
                                    
                                    const validHistory = progressHistory.filter(h => h && h.date && h.progress !== undefined && h.progress > 0);
                                    if (validHistory.length === 0) return null;

                                    const sortedHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
                                    const lastRecord = sortedHistory[sortedHistory.length - 1];
                                    if (!lastRecord) return null;

                                    // Find matching week point in curveData
                                    const curvePoint = curveData.find(pt => {
                                        const ptTime = new Date(pt.date + "T00:00:00").getTime();
                                        const recordTime = new Date(lastRecord.date + "T00:00:00").getTime();
                                        return recordTime >= ptTime && recordTime < ptTime + 7 * 86400000;
                                    });
                                    const weekIndex = curvePoint ? curvePoint.weekIndex : sortedHistory.length;
                                    const activeBaseline = baselines.find(b => b.vigente);
                                    const activeTasks = activeBaseline ? activeBaseline.tasksSnapshot : tasks;
                                    const planPct = calculateProgrammedProgressAtDate(activeTasks, lastRecord.date);
                                    const realPct = lastRecord.progress;
                                    const diff = realPct - planPct;

                                    const formattedDate = new Date(lastRecord.date + "T12:00:00").toLocaleDateString('es-CL', { day: 'numeric', month: 'long' });
                                    
                                    return (
                                        <div className="mt-6 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
                                            <div className="flex flex-col pr-4 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 pb-3 md:pb-0 min-w-[150px]">
                                                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Corte Actual</span>
                                                <span className="text-base font-black text-slate-800 dark:text-white mt-0.5">Semana {weekIndex}</span>
                                                <span className="text-xs text-slate-500 mt-0.5">{formattedDate}</span>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 sm:grid-cols-5 flex-1 pl-0 md:pl-6 gap-4 sm:gap-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">% Programado</span>
                                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-1">{truncateProgress(planPct)}%</span>
                                                </div>
                                                
                                                <div className="flex flex-col">
                                                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">% Real</span>
                                                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400 mt-1">{truncateProgress(realPct)}%</span>
                                                </div>
                                                
                                                <div className="flex flex-col">
                                                     <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Diff (Diferencia)</span>
                                                     <span className={`text-sm font-bold mt-1 ${diff >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                                                         {diff >= 0 ? '+' : ''}{truncateProgress(diff)}%
                                                     </span>
                                                 </div>
                                                 
                                                 {(() => {
                                                     const calculatedDelayCS = calculateDelayDays(curveData, realPct, lastRecord.date);
                                                     const delayCSVal = (lastRecord.delayCurvaS && lastRecord.delayCurvaS > 0) ? lastRecord.delayCurvaS : calculatedDelayCS;
                                                     const delayRCVal = getCriticalPathDelayAtDate(lastRecord.date);

                                                     return (
                                                         <>
                                                             <div className="flex flex-col">
                                                                 <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Atraso C.S</span>
                                                                 <span className={`text-sm font-bold mt-1 ${delayCSVal > 0 ? "text-red-500 font-extrabold" : "text-slate-500 dark:text-slate-400"}`}>
                                                                     {delayCSVal}d
                                                                 </span>
                                                             </div>
                                                             
                                                             <div className="flex flex-col col-span-2 sm:col-span-1">
                                                                 <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Atraso R.C</span>
                                                                 <span className={`text-sm font-bold mt-1 ${delayRCVal > 0 ? "text-orange-500 font-extrabold" : "text-slate-500 dark:text-slate-400"}`}>
                                                                     {delayRCVal}d
                                                                 </span>
                                                             </div>
                                                         </>
                                                     );
                                                 })()}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="hidden md:block xl:col-span-1">
                            <div className="h-full rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-[#1a2634] flex flex-col">
                                <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-700">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Actividad Reciente</h3>
                                </div>
                                <div className="p-6 flex-1 overflow-y-auto max-h-[400px]">
                                    {activities.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                                            <span className="material-symbols-outlined text-4xl mb-2">history</span>
                                            <p className="text-sm italic text-center">No hay actividad registrada aún en el proyecto.</p>
                                        </div>
                                    ) : (
                                        <ol className="relative border-l border-slate-200 dark:border-slate-700">
                                            {activities.map((act) => (
                                                <li key={act.id} className="mb-6 ml-6">
                                                    <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white dark:ring-[#1a2634] ${act.type === 'planning' ? 'bg-blue-100 text-blue-600' :
                                                        act.type === 'quality' ? 'bg-orange-100 text-orange-600' :
                                                            act.type === 'payments' ? 'bg-emerald-100 text-emerald-600' :
                                                                'bg-slate-100 text-slate-600'
                                                        }`}>
                                                        <span className="material-symbols-outlined text-[14px]">
                                                            {act.type === 'planning' ? 'calendar_month' :
                                                                act.type === 'quality' ? 'fact_check' :
                                                                    act.type === 'payments' ? 'handshake' : 'notifications'}
                                                        </span>
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="text-sm font-semibold text-slate-800 dark:text-white leading-tight">{act.title}</h3>
                                                        {(act.count || 1) > 1 && (
                                                            <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                                                x{act.count}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <time className="mb-1 block text-[10px] font-normal leading-none text-slate-400 uppercase mt-1">
                                                        {new Date(act.timestamp).toLocaleString('es-CL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                                                    </time>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{act.description}</p>
                                                    <p className="text-[10px] text-slate-400 mt-1 font-medium">Por: {act.user}</p>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>
);
};

export default DashboardScreen;